import { localeCulture, localeDirection, normalizeUiLocale, readRuntimeUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';

/**
 * Opens a submission in an isolated same-origin document before invoking the
 * browser print dialog. Printing the live DNN page is unreliable because skin
 * wrappers may constrain width/height or hide overflow, which can clip the
 * second half of long forms in Chrome/Edge PDF output.
 */

interface PrintOptions {
  title?: string;
  closeAfterPrint?: boolean;
}

const MODULE_CSS_PATH = '/desktopmodules/faradynamicforms/contents/react/';

function copyRuntimeAttributes(sourceRoot: HTMLElement | null, targetRoot: HTMLElement): void {
  targetRoot.setAttribute('data-dynamicforms-react-root', '');
  targetRoot.setAttribute('data-df-print-document', 'true');
  targetRoot.className = sourceRoot?.className ?? 'df-bs-3';

  if (!sourceRoot) return;
  for (const attribute of Array.from(sourceRoot.attributes)) {
    if (attribute.name === 'id' || attribute.name === 'data-react-mounted') continue;
    targetRoot.setAttribute(attribute.name, attribute.value);
  }
  targetRoot.setAttribute('data-df-print-document', 'true');
}

function removeVerificationFieldsFromPrintClone(clone: HTMLElement): void {
  clone.querySelectorAll<HTMLElement>('[data-field-type="captcha"], [data-field-type="simplecaptcha"]').forEach((node) => node.remove());

  // Compatibility fallback for an older cached renderer that did not emit
  // data-field-type on the wrapper. Remove the closest field group so neither
  // the captcha control nor its label/required marker can leak into output.
  clone.querySelectorAll<HTMLElement>('.df-recaptcha, .df-simple-captcha').forEach((node) => {
    const wrapper = node.closest<HTMLElement>('.df-form-group');
    if (wrapper) wrapper.remove();
    else node.remove();
  });
}

function syncFormControlState(source: HTMLElement, clone: HTMLElement): void {
  const sourceControls = Array.from(source.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select'));
  const cloneControls = Array.from(clone.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select'));

  sourceControls.forEach((control, index) => {
    const cloned = cloneControls[index];
    if (!cloned) return;

    if (control.tagName === 'INPUT' && cloned.tagName === 'INPUT') {
      const sourceInput = control as HTMLInputElement;
      const clonedInput = cloned as HTMLInputElement;
      clonedInput.value = sourceInput.value;
      clonedInput.checked = sourceInput.checked;
      return;
    }
    if (control.tagName === 'TEXTAREA' && cloned.tagName === 'TEXTAREA') {
      const sourceTextArea = control as HTMLTextAreaElement;
      const clonedTextArea = cloned as HTMLTextAreaElement;
      clonedTextArea.value = sourceTextArea.value;
      clonedTextArea.textContent = sourceTextArea.value;
      return;
    }
    if (control.tagName === 'SELECT' && cloned.tagName === 'SELECT') {
      const sourceSelect = control as HTMLSelectElement;
      const clonedSelect = cloned as HTMLSelectElement;
      Array.from(clonedSelect.options).forEach((option, optionIndex) => {
        option.selected = sourceSelect.options[optionIndex]?.selected === true;
      });
    }
  });
}

function moduleStylesheets(): HTMLLinkElement[] {
  return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"][href]'))
    .filter((link) => link.href.toLowerCase().includes(MODULE_CSS_PATH));
}

function appendStylesheet(targetDocument: Document, href: string): Promise<void> {
  return new Promise((resolve) => {
    const link = targetDocument.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
    targetDocument.head.appendChild(link);
    window.setTimeout(resolve, 4000);
  });
}

async function waitForImages(targetDocument: Document): Promise<void> {
  const waits = Array.from(targetDocument.images).map(async (image) => {
    if (image.complete) return;
    await new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
      window.setTimeout(resolve, 5000);
    });
  });
  await Promise.all(waits);
}

async function waitForFonts(targetDocument: Document): Promise<void> {
  const fonts = targetDocument.fonts;
  if (!fonts) return;
  await Promise.race([
    fonts.ready.then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, 4000))
  ]);
}

/**
 * Opens an isolated print document that contains only the requested DynamicForms
 * section. The new document uses the module's own CSS and print contract, not
 * the surrounding DNN skin, so long multi-page forms are not clipped.
 */
export function printDynamicFormsElement(element: HTMLElement | null, options: PrintOptions = {}): void {
  const sourceRoot = element?.closest<HTMLElement>('[data-dynamicforms-react-root]') ?? null;
  const locale = normalizeUiLocale(sourceRoot?.dataset.uiLocale || sourceRoot?.dataset.culture || readRuntimeUiLocale());
  const copy = publicCopy(locale);
  if (!element) {
    window.alert(copy.printableContentNotFound);
    return;
  }

  const popup = window.open('', '_blank', 'width=1100,height=820,scrollbars=yes,resizable=yes');
  if (!popup) {
    window.alert(copy.popupBlocked);
    return;
  }
  try { popup.opener = null; } catch { /* Best effort for older browsers. */ }

  const printDocument = popup.document;
  printDocument.open();
  printDocument.write('<!doctype html><html lang="' + localeCulture(locale) + '" dir="' + localeDirection(locale) + '"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base href="' + document.baseURI.replace(/"/g, '&quot;') + '"></head><body class="df-isolated-print-body"></body></html>');
  printDocument.close();
  printDocument.title = options.title?.trim() || document.title || 'DynamicForms';

  const printRoot = printDocument.createElement('main');
  copyRuntimeAttributes(sourceRoot, printRoot);
  printRoot.classList.add('df-print-document');

  const clone = element.cloneNode(true) as HTMLElement;
  clone.querySelectorAll<HTMLElement>('.no-print').forEach((node) => node.remove());
  syncFormControlState(element, clone);
  removeVerificationFieldsFromPrintClone(clone);
  printRoot.appendChild(clone);
  printDocument.body.appendChild(printRoot);

  const currentStylesheets = moduleStylesheets();
  const hrefs = Array.from(new Set(currentStylesheets.map((link) => link.href)));
  void (async () => {
    await Promise.all(hrefs.map((href) => appendStylesheet(printDocument, href)));
    await Promise.all([waitForImages(printDocument), waitForFonts(printDocument)]);
    await new Promise<void>((resolve) => popup.requestAnimationFrame(() => popup.requestAnimationFrame(() => resolve())));
    popup.focus();
    popup.print();

    if (options.closeAfterPrint !== false) {
      popup.addEventListener('afterprint', () => popup.close(), { once: true });
      window.setTimeout(() => { if (!popup.closed) popup.close(); }, 120000);
    }
  })().catch(() => {
    popup.close();
    window.alert(copy.printPreparationFailed);
  });
}
